import clsx from "clsx";
import type { CSSProperties, FC, HTMLAttributes } from "react";

import { PremadeInfoStatus } from "@dndbeyond/character-rules-engine";
import EyeIcon from "@dndbeyond/fontawesome-cache/svgs/solid/eye.svg";
import { SpinnerIcon } from "@dndbeyond/ttui/components/Icons";
import { Badge } from "@dndbeyond/ttui/lexicon/components/Badge";
import { Button, ButtonProps } from "@dndbeyond/ttui/lexicon/components/Button";
import { Tooltip } from "@dndbeyond/ttui/lexicon/components/Tooltip";
import { useTooltip } from "@dndbeyond/ttui/lexicon/hooks/useTooltip";

import styles from "./styles.module.css";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  characterLevel: number;
  characterName: string;
  characterId?: number;
  primaryClassName: string;
  speciesName: string;
  longDescription: string;
  sourceName: string;
  imageUrl: string;
  imageAlt: string;
  primaryButtonProps: ButtonProps;
  primaryButtonText?: string;
  isClaimInProgress: boolean;
  previewLink: string;
  publishStatus: PremadeInfoStatus;
}

export const Card: FC<CardProps> = ({
  publishStatus,
  characterLevel,
  characterName,
  characterId,
  primaryClassName,
  speciesName,
  longDescription,
  sourceName,
  imageUrl,
  imageAlt,
  primaryButtonText = "Choose",
  primaryButtonProps,
  isClaimInProgress,
  previewLink,
  className,
  ...props
}) => {
  const { anchorProps, tooltipProps } = useTooltip("tooltip-id");
  // Convert literal \n strings to actual newlines
  const formattedDescription = longDescription.replace(/\\n/g, "\n");

  return (
    <div
      className={clsx([
        styles.card,
        styles[publishStatus.toLowerCase()],
        className,
      ])}
      style={{ "--background-image": `url(${imageUrl})` } as CSSProperties}
      aria-label={imageAlt}
      {...props}
    >
      {publishStatus !== PremadeInfoStatus.PUBLISHED && (
        <Badge
          className={styles.statusBadge}
          borderColor={
            publishStatus === PremadeInfoStatus.DRAFT
              ? "var(--lexicon_magenta-600)"
              : "var(--lexicon_grey-300)"
          }
          label={publishStatus}
        />
      )}
      <div className={styles.gradient} />
      <div className={styles.content}>
        <h2 className={styles.title} id={characterId?.toString()}>
          {characterName}
        </h2>
        <div className={styles.badgeGroup}>
          <Badge
            className={styles.badge}
            label={`${primaryClassName} Lvl. ${characterLevel}`}
          />
          <Badge className={styles.badge} label={speciesName} />
        </div>

        <p className={styles.description}>{formattedDescription}</p>
        <div className={styles.footer}>
          <span className={styles.source}>{sourceName}</span>
          <div className={styles.actions}>
            <>
              <Button
                size="small"
                color="secondary"
                href={previewLink}
                aria-label={`Preview ${characterName}: ${speciesName} ${primaryClassName}`}
                {...anchorProps}
              >
                <EyeIcon />
              </Button>
              <Tooltip {...tooltipProps}>Preview character sheet</Tooltip>
            </>
            <Button
              className={styles.claimButton}
              size="small"
              color="primary"
              variant="rightArrow"
              {...primaryButtonProps}
            >
              {isClaimInProgress ? <SpinnerIcon /> : primaryButtonText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
